import React from "react";
import { useAtomValue } from "jotai";
import { selectedProductDetailsAtom, selectedProductsLoadingAtom } from "@/atoms/selectedProductsAtom";
import { financialApplicationIdAtom } from "@/atoms/financialApplicationIdAtom";
import { SelectedProductCard } from "./SelectedProductCard";
import { LoadingV2 } from "@ucl/library";
import { useDepositApplicationSelections } from "@/hooks/useDepositApplicationSelections";
import { useFinancialProducts } from "@hooks/useFinancialProducts";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";
import "./SelectProductFormStepSidebar.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faUser } from "@fortawesome/free-solid-svg-icons";

export const SelectProductFormStepSidebar: React.FC = () => {
  // Get selected products from jotai atom
  const selectedProductDetails = useAtomValue(selectedProductDetailsAtom);
  const loading = useAtomValue(selectedProductsLoadingAtom);
  const financialApplicationId = useAtomValue(financialApplicationIdAtom);

  // Get API client and products for delete functionality
  const apiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);
  const { products } = useFinancialProducts(apiClient);
  
  const { setSelectedProductIds } = useDepositApplicationSelections(
    financialApplicationId || "",
    apiClient,
    products
  );

  const handleDelete = (depositApplicationId: string) => {
    // Find the product to be deleted
    const productToDelete = selectedProductDetails.find(
      item => item.depositApplicationId === depositApplicationId
    );
    
    if (productToDelete) {
      // Get current selected product IDs (using the actual financialProductId)
      const currentSelectedIds = selectedProductDetails.map(item => item.product.id);
      
      // Remove the deleted product from the selection
      const updatedSelectedIds = currentSelectedIds.filter(
        id => id !== productToDelete.product.id
      );
      
      // Update the selection
      setSelectedProductIds(updatedSelectedIds);
    }
  };

  // Group products by product type using boolean properties
  const businessProducts = selectedProductDetails.filter(
    detail => detail.product.isBusinessProduct || detail.product.isIoltaProduct
  );
  const personalProducts = selectedProductDetails.filter(
    detail => detail.product.isPersonalProduct
  );

  // Determine if we need to show organizational sections
  const showOrganizationalSections = businessProducts.length > 0 && personalProducts.length > 0;

  if (loading) {
    return (
      <div className="select-product-form-step-sidebar">
        <div className="select-product-form-step-sidebar__content">
          <LoadingV2 />
        </div>
      </div>
    );
  }

  if (selectedProductDetails.length === 0) {
    return (
      <div className="select-product-form-step-sidebar">
        <div className="select-product-form-step-sidebar__content">
          <div className="select-product-form-step-sidebar__empty">
            <p>You have not yet selected any products. Please select one or more products to continue.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="select-product-form-step-sidebar">
      <div className="select-product-form-step-sidebar__content">
        {showOrganizationalSections ? (
          <>
            {/* Business Accounts Section */}
            {businessProducts.length > 0 && (
              <div className="select-product-form-step-sidebar__section">
                <h3 className="select-product-form-step-sidebar__section-title">
                  <FontAwesomeIcon icon={faBuilding} />
                  Business Accounts
                </h3>
                <div className="select-product-form-step-sidebar__products">
                  {businessProducts.map(({ product, depositApplicationId }) => (
                    <SelectedProductCard 
                      key={depositApplicationId} 
                      product={{
                        id: product.productSlug,
                        title: product.productName,
                        description: product.productDescription,
                        features: product.productFeatures,
                        customerSegment: product.customerSegment,
                      }}
                      depositApplicationId={depositApplicationId}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Personal Accounts Section */}
            {personalProducts.length > 0 && (
              <div className="select-product-form-step-sidebar__section">
                <h3 className="select-product-form-step-sidebar__section-title">
                  <FontAwesomeIcon icon={faUser} />
                  Personal Accounts
                </h3>
                <div className="select-product-form-step-sidebar__products">
                  {personalProducts.map(({ product, depositApplicationId }) => (
                    <SelectedProductCard 
                      key={depositApplicationId} 
                      product={{
                        id: product.productSlug,
                        title: product.productName,
                        description: product.productDescription,
                        features: product.productFeatures,
                        customerSegment: product.customerSegment,
                      }}
                      depositApplicationId={depositApplicationId}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          /* Single section when only one type is selected */
          <div className="select-product-form-step-sidebar__products">
            {selectedProductDetails.map(({ product, depositApplicationId }) => (
              <SelectedProductCard 
                key={depositApplicationId} 
                product={{
                  id: product.productSlug,
                  title: product.productName,
                  description: product.productDescription,
                  features: product.productFeatures,
                  customerSegment: product.customerSegment,
                }}
                depositApplicationId={depositApplicationId}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
